import type {
  YearlyMonth,
  YearlyPerformance,
} from '@/entities/performance/api/performance.api';
import {
  getPendingMonths,
  toYearlyTrendPoints,
  MONTHLY_STATUS_LABEL,
  toYm,
  type MonthCardStatus,
} from '@/entities/performance/lib/performance';
import { cn } from '@/shared/lib/utils';
import {
  EMPTY,
  formatPercent,
  formatShortYm,
  formatWon,
} from '@/shared/utils/format';
import { useMemo, useState } from 'react';
import { PERF_BADGE, PerfBadge, TrendLegend } from './PerformanceParts';
import { TrendChart, type TrendMode } from './TrendChart';

/* ───────────── KPI 4종 ───────────── */

/**
 * KPI 4종 — 합계 · 정산 비율은 /yearly 서버 값. 정산 전 · 미등록은 월 카드 상태로 센다.
 * 목표 · 전년 비교는 서버가 아직 주지 않아 그리지 않는다 (임의 값으로 채우지 않음).
 */
export const AnnualKpis = ({ yearly }: { yearly: YearlyPerformance }) => {
  const pendingMonths = getPendingMonths(yearly.months);
  const pendingText = pendingMonths
    .map(
      (m) =>
        `${m.month}월 ${m.status === 'UNREGISTERED' ? '처방 미등록' : '정산 전'}`,
    )
    .join(' · ');

  const items = [
    {
      label: '연간 처방 예상 금액',
      value: formatWon(yearly.estimatedAmountPrescription),
      sub: null,
      subClass: '',
    },
    {
      label: '연간 정산 확정 금액',
      value: formatWon(yearly.confirmedAmountSettlement),
      sub: null,
      subClass: '',
    },
    {
      label: '연간 정산 비율',
      value:
        yearly.settlementRatio === null
          ? EMPTY
          : `${Math.round(yearly.settlementRatio)}%`,
      sub: null,
      subClass: '',
    },
    {
      label: '정산 전 · 미등록',
      value: `${pendingMonths.length}개월`,
      sub: pendingText,
      subClass: 'text-[#767676]',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-5">
      {items.map((it) => (
        <div
          key={it.label}
          className="flex min-h-[108px] min-w-0 flex-col rounded-xl bg-surface px-4 py-4 lg:px-[22px] lg:py-5"
        >
          <p className="text-xs leading-[19px] text-[#8d8d8d] lg:text-[12.5px]">
            {it.label}
          </p>
          <p className="mt-2 truncate text-lg leading-8 font-bold text-ink lg:text-2xl lg:leading-9">
            {it.value}
          </p>
          {it.sub && (
            <p
              className={cn(
                'mt-1.5 text-[11.5px] leading-[17px] font-medium',
                it.subClass,
              )}
            >
              {it.sub}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

/* ───────────── 실적 금액 추이 ───────────── */

const SEGMENTS: { value: TrendMode; label: string }[] = [
  { value: 'prescription', label: '처방' },
  { value: 'settlement', label: '정산' },
  { value: 'both', label: '함께 보기' },
];

/** 진하게 칠할 달 = 정산이 끝난 마지막 달 */
const latestSettledYm = (rows: YearlyMonth[]) =>
  rows
    .filter((r) => r.monthlyStatusCode === 'SETTLED')
    .map((r) => r.settlementYearMonth)
    .sort()
    .reverse()[0];

export const AnnualTrendCard = ({
  year,
  rows,
  onSelectMonth,
}: {
  year: number;
  rows: YearlyMonth[];
  onSelectMonth: (ym: string) => void;
}) => {
  const [mode, setMode] = useState<TrendMode>('both');
  const points = useMemo(() => toYearlyTrendPoints(rows), [rows]);

  return (
    <section className="rounded-card bg-surface px-4 pt-5 pb-4 lg:px-7 lg:pt-6 lg:pb-5">
      <div className="flex flex-col gap-3 lg:h-[34px] lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <h2 className="text-lg leading-[27px] font-bold text-ink">
            실적 금액 추이
          </h2>
          <TrendLegend />
        </div>
        <div
          role="radiogroup"
          aria-label="표시 기준"
          className="flex w-fit rounded-[9px] bg-muted p-1"
        >
          {SEGMENTS.map((s) => (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={mode === s.value}
              onClick={() => setMode(s.value)}
              className={cn(
                'rounded-[7px] px-3.5 py-[7px] text-[12.5px] leading-[19px]',
                mode === s.value
                  ? 'bg-surface font-bold text-ink shadow-[0_1px_3px_rgb(0_0_0/10%)]'
                  : 'text-[#8d8d8d]',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      {/* 모바일은 차트 영역만 가로 스크롤 (페이지 가로 스크롤 금지, README §8) */}
      <div className="mt-[18px] overflow-x-auto">
        <div className="min-w-[640px]">
          <TrendChart
            year={year}
            points={points}
            mode={mode}
            fullYear
            highlightYm={latestSettledYm(rows)}
            height={222}
            barSize={30}
            onSelectMonth={onSelectMonth}
          />
        </div>
      </div>
    </section>
  );
};

/* ───────────── 월별 실적 카드 12장 ───────────── */

const statusBadge = (status: MonthCardStatus, ratio: number | null) => {
  switch (status) {
    case 'SETTLED':
      return (
        <PerfBadge className={PERF_BADGE.settled}>
          {MONTHLY_STATUS_LABEL.SETTLED}{' '}
          {formatPercent(ratio === null ? null : Math.round(ratio))}
        </PerfBadge>
      );
    case 'PENDING_CONFIRMATION':
      return (
        <PerfBadge className={PERF_BADGE.pending}>
          {MONTHLY_STATUS_LABEL[status]}
        </PerfBadge>
      );
    case 'BEFORE_SETTLEMENT':
      return (
        <PerfBadge className={PERF_BADGE.info}>
          {MONTHLY_STATUS_LABEL[status]}
        </PerfBadge>
      );
    case 'UNREGISTERED':
      return (
        <PerfBadge className={PERF_BADGE.muted}>
          {MONTHLY_STATUS_LABEL[status]}
        </PerfBadge>
      );
    default:
      return (
        <PerfBadge className={PERF_BADGE.faint}>
          {MONTHLY_STATUS_LABEL.NOT_ARRIVED}
        </PerfBadge>
      );
  }
};

const AmountBlock = ({
  kind,
  value,
  faint,
  dim,
}: {
  kind: 'prescription' | 'settlement';
  value: string;
  faint: boolean;
  dim: boolean;
}) => (
  <div className="flex flex-col gap-[3px]">
    <div className="flex items-center gap-1.5">
      <PerfBadge
        className={
          faint
            ? PERF_BADGE.faint
            : kind === 'prescription'
              ? PERF_BADGE.info
              : PERF_BADGE.settled
        }
      >
        {kind === 'prescription' ? '처방' : '정산'}
      </PerfBadge>
      <span
        className={cn(
          'text-[11px] leading-[17px]',
          faint ? 'text-[#c6c6c6]' : 'text-[#8d8d8d]',
        )}
      >
        {kind === 'prescription' ? '예상 금액' : '확정 금액'}
      </span>
    </div>
    <p
      className={cn(
        'text-[15px] leading-[23px] font-bold',
        faint ? 'text-[#c6c6c6]' : dim ? 'text-[#b8b8b8]' : 'text-ink',
      )}
    >
      {value}
    </p>
  </div>
);

export const MonthCardGrid = ({
  year,
  rows,
  onSelectMonth,
}: {
  year: number;
  rows: YearlyMonth[];
  onSelectMonth: (ym: string) => void;
}) => {
  const byYm = new Map(rows.map((r) => [r.settlementYearMonth, r]));

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-6 lg:gap-4">
      {Array.from({ length: 12 }, (_, i) => {
        const ym = toYm(year, i + 1);
        const row = byYm.get(ym);
        const status: MonthCardStatus = row?.monthlyStatusCode ?? 'NOT_ARRIVED';
        const future = !row;
        const unregistered = status === 'UNREGISTERED';
        // 확정 금액은 정산이 끝난 달만 보여준다 (처방 확인중 · 정산 전은 —)
        const estimated =
          future || unregistered
            ? EMPTY
            : formatWon(row.estimatedAmountPrescription);
        const confirmed =
          status === 'SETTLED'
            ? formatWon(row?.confirmedAmountSettlement)
            : EMPTY;

        const body = (
          <>
            <p
              className={cn(
                'text-sm leading-[21px] font-bold',
                future ? 'text-[#c6c6c6]' : 'text-ink',
              )}
            >
              {formatShortYm(ym)} 실적
            </p>
            <div className="mt-2">
              {statusBadge(status, row?.settlementRatio ?? null)}
            </div>
            <div className="mt-3.5 flex flex-col gap-2.5">
              <AmountBlock
                kind="prescription"
                value={estimated}
                faint={future}
                dim={unregistered}
              />
              <AmountBlock
                kind="settlement"
                value={confirmed}
                faint={future}
                dim={unregistered}
              />
            </div>
          </>
        );

        // 미도래 달은 누를 수 없다
        return future ? (
          <div
            key={ym}
            aria-disabled
            className="flex min-h-[203px] flex-col rounded-xl border border-dashed border-line bg-[#fafafa] px-[18px] py-4"
          >
            {body}
          </div>
        ) : (
          <button
            key={ym}
            type="button"
            onClick={() => onSelectMonth(ym)}
            className="flex min-h-[203px] flex-col rounded-xl border border-transparent bg-surface px-[18px] py-4 text-left transition-colors hover:border-line-strong"
          >
            {body}
          </button>
        );
      })}
    </div>
  );
};
